import {getStatsData} from "@/api/stats/stats-services";
import {loadStatsUniverseBatched} from "@/pages/stats/stats-aggregation";
import {
    getComplaintForm,
    getForms,
    getQuestionsByForm,
} from "@/api/forms-questions/forms-questions-services";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import Loader from "@/components/utils/loader";
import {format} from "date-fns";
import {es} from "date-fns/locale";
import {Calendar as CalendarIcon, X} from "lucide-react";

import {Button} from "@/components/ui/button";
import {Calendar} from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {cn} from "@/lib/utils";
import {useStore} from "@/store";
import QuestionOptionCharts, {
    buildQuestionOptionCharts,
    getQuestionChartStorageKey,
} from "@/pages/stats/question-option-charts";
import StatsChartSettings, {
    getStatsChartVisibility,
    useStatsChartPreferences,
} from "@/pages/stats/stats-chart-settings";
import StatsSummaryTable from "@/pages/stats/stats-summary-table";

import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    LabelList,
} from "recharts";

const ANONYMOUS_ALIAS_PATTERNS = [
    /(^|_)do_you_want_to_remain_anonymous(_|$)/i,
    /(^|_)desea_permanecer_en_el_anonimato(_|$)/i,
    /(^|_)deseja_permanecer_anonim[oa](_|$)/i,
];

const ANONYMOUS_QUESTION_TEXTS = [
    "do you want to remain anonymous",
    "would you like to remain anonymous",
    "desea permanecer en el anonimato",
    "deseja permanecer anonimo",
];

const unwrapListResponse = (value, depth = 0) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (!value || typeof value !== "object" || depth > 3) {
        return [];
    }

    for (const wrapperKey of ["data", "items", "members", "results", "communications", "questions", "answers"]) {
        if (Array.isArray(value[wrapperKey])) {
            return value[wrapperKey];
        }
    }

    for (const wrapperKey of ["data", "items", "members", "results"]) {
        const nested = value[wrapperKey];
        if (nested && typeof nested === "object") {
            const unwrapped = unwrapListResponse(nested, depth + 1);
            if (unwrapped.length > 0) {
                return unwrapped;
            }
        }
    }

    return [];
};

const normalizeCommunicationsResponse = (response) => {
    try {
        return unwrapListResponse(response);
    } catch (error) {
        return [];
    }
};

const normalizeFormsResponse = (response) => {
    try {
        const list = unwrapListResponse(response);
        if (list.length > 0) {
            return list.filter((form) => form && typeof form === "object");
        }

        if (response && typeof response === "object" && !Array.isArray(response)) {
            const values = Object.values(response).filter(
                (value) => value && typeof value === "object" && !Array.isArray(value),
            );
            if (values.some((value) => value.type !== undefined || value.id !== undefined)) {
                return values;
            }
        }

        return [];
    } catch (error) {
        return [];
    }
};

const normalizeQuestionsResponse = (response) => {
    try {
        const list = unwrapListResponse(response);
        if (list.length > 0) {
            return list.filter((question) => question && typeof question === "object");
        }

        if (response && typeof response === "object" && !Array.isArray(response)) {
            const directQuestion = response.id !== undefined || response.alias !== undefined;
            if (directQuestion) {
                return [response];
            }
        }

        return [];
    } catch (error) {
        return [];
    }
};

const normalizeFormType = (form) => {
    try {
        return String(form?.type ?? "").trim().toLowerCase();
    } catch (error) {
        return "";
    }
};

const isComplaintFormType = (normalizedType) =>
    normalizedType === "complaint" ||
    normalizedType === "complaints" ||
    normalizedType === "denuncia" ||
    normalizedType === "denuncias";

const isConsultationFormType = (normalizedType) =>
    normalizedType === "consultation" ||
    normalizedType === "consultations" ||
    normalizedType === "inquiry" ||
    normalizedType === "inquiries" ||
    normalizedType === "query" ||
    normalizedType === "queries" ||
    normalizedType === "consulta" ||
    normalizedType === "consultas";

const normalizeAnswerValue = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

const normalizeQuestionValue = (value) =>
    normalizeAnswerValue(value)
        .replace(/[^a-z0-9_ ]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const isAnonymousQuestion = (question) => {
    const normalizedAlias = normalizeQuestionValue(question?.alias ?? "");
    if (
        ANONYMOUS_ALIAS_PATTERNS.some((pattern) => pattern.test(normalizedAlias))
    ) {
        return true;
    }

    if (!Array.isArray(question?.texts)) {
        return false;
    }

    return question.texts.some((text) =>
        ANONYMOUS_QUESTION_TEXTS.includes(normalizeQuestionValue(text?.text ?? "")),
    );
};

const getAnonymousAnswer = (communication) => {
    if (!Array.isArray(communication?.answers)) {
        return null;
    }

    return (
        communication.answers.find((answer) => isAnonymousQuestion(answer?.question)) ??
        null
    );
};

const getSelectedOptionText = (answer) => {
    if (!Array.isArray(answer?.question?.options)) {
        return "";
    }

    const rawAnswerValues = [answer?.answerText, answer?.answer]
        .filter((value) => value !== undefined && value !== null && value !== "")
        .map((value) => String(value));

    const selectedOption = answer.question.options.find(
        (option) =>
            rawAnswerValues.includes(String(option?.value ?? "")) ||
            rawAnswerValues.includes(String(option?.id ?? "")),
    );

    if (!selectedOption) {
        return "";
    }

    if (typeof selectedOption.text === "string") {
        return selectedOption.text;
    }

    if (!Array.isArray(selectedOption.text)) {
        return "";
    }

    return selectedOption.text.find((text) => text?.language === "es")?.text ?? selectedOption.text[0]?.text ?? "";
};

const getAnonymousStatusFromValue = (value) => {
    const normalizedValue = normalizeAnswerValue(value);

    if (!normalizedValue) {
        return null;
    }

    if (
        [
            "1",
            "true",
            "yes",
            "si",
            "sim",
            "anonymous",
            "anonima",
            "anonimas",
            "anonimo",
            "anonimos",
        ].includes(normalizedValue)
    ) {
        return true;
    }

    if (
        [
            "0",
            "false",
            "no",
            "nao",
            "not anonymous",
            "non anonymous",
            "no anonima",
            "no anonimas",
            "no anonimo",
            "no anonimos",
        ].includes(normalizedValue)
    ) {
        return false;
    }

    return null;
};

const getAnonymousStatus = (answer) => {
    if (!answer) {
        return null;
    }

    const optionTextStatus = getAnonymousStatusFromValue(getSelectedOptionText(answer));
    if (optionTextStatus !== null) {
        return optionTextStatus;
    }

    return (
        getAnonymousStatusFromValue(answer?.answerText) ??
        getAnonymousStatusFromValue(answer?.answer)
    );
};

const getCommunicationDateValue = (communication) => {
    const rawDate = communication?.date ?? communication?.createdAt ?? null;

    if (typeof rawDate !== "string") {
        return null;
    }

    const normalizedDate = rawDate.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(normalizedDate) ? normalizedDate : null;
};

export const filterCommunicationsByDate = (communications, dateStart, dateEnd) => {
    if (!Array.isArray(communications)) {
        return [];
    }

    return communications.filter((communication) => {
        const communicationDate = getCommunicationDateValue(communication);

        if (!communicationDate) {
            return !dateStart && !dateEnd;
        }

        if (dateStart && communicationDate < dateStart) {
            return false;
        }

        if (dateEnd && communicationDate > dateEnd) {
            return false;
        }

        return true;
    });
};

export const buildAnonymousPieData = (communications, t) => {
    const counts = (Array.isArray(communications) ? communications : []).reduce(
        (result, communication) => {
            const anonymousStatus = getAnonymousStatus(getAnonymousAnswer(communication));

            if (anonymousStatus === true) {
                result.anonymous += 1;
            } else if (anonymousStatus === false) {
                result.nonAnonymous += 1;
            }

            return result;
        },
        {anonymous: 0, nonAnonymous: 0},
    );

    return [
        counts.anonymous > 0
            ? {name: t("statsPage.graph3.anonymous"), value: counts.anonymous}
            : null,
        counts.nonAnonymous > 0
            ? {name: t("statsPage.graph3.nonAnonymous"), value: counts.nonAnonymous}
            : null,
    ].filter(Boolean);
};

const loadQuestionsForForm = async (form) => {
    const formId = form?.id ?? form?.form_id;
    if (formId === null || formId === undefined || formId === "") {
        return normalizeQuestionsResponse(form?.questions);
    }

    try {
        const questions = await getQuestionsByForm(formId);
        const normalized = normalizeQuestionsResponse(questions);
        if (normalized.length > 0) {
            return normalized;
        }
    } catch (error) {
        console.error("Error loading questions for statistics form:", formId, error);
    }

    try {
        const embeddedQuestions = normalizeQuestionsResponse(form?.questions);
        if (embeddedQuestions.length > 0) {
            return embeddedQuestions;
        }
    } catch (error) {
        // Ignorar y continuar con el detalle del formulario.
    }

    try {
        const formDetail = await getComplaintForm(formId);
        const detailQuestions =
            normalizeQuestionsResponse(formDetail?.questions) ||
            [];
        if (detailQuestions.length > 0) {
            return detailQuestions;
        }
        return normalizeQuestionsResponse(formDetail);
    } catch (error) {
        console.error("Error loading form detail for statistics:", formId, error);
        return [];
    }
};

const loadQuestionCatalog = async () => {
    try {
        const formsResponse = await getForms();
        const forms = normalizeFormsResponse(formsResponse);
        const complaintForm = forms.find((form) => isComplaintFormType(normalizeFormType(form)));
        const consultationForm = forms.find((form) =>
            isConsultationFormType(normalizeFormType(form)),
        );
        const [complaintQuestions, consultationQuestions] = await Promise.all([
            complaintForm ? loadQuestionsForForm(complaintForm) : Promise.resolve([]),
            consultationForm
                ? loadQuestionsForForm(consultationForm)
                : Promise.resolve([]),
        ]);

        return {
            complaint: normalizeQuestionsResponse(complaintQuestions),
            consultation: normalizeQuestionsResponse(consultationQuestions),
        };
    } catch (error) {
        console.error("Error loading questions for statistics:", error);
        return {complaint: [], consultation: []};
    }
};

const StatsPage = () => {
    const current_year = new Date().getFullYear();
    const currentDate = new Date();

    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [data, setData] = useState({});
    const [universeAggregates, setUniverseAggregates] = useState({
        questionOptionCharts: [],
        anonymousPies: {complaint: [], consultation: []},
        summary: null,
    });
    const [questionCatalog, setQuestionCatalog] = useState({
        complaint: [],
        consultation: [],
    });
    const questionCatalogRef = useRef(questionCatalog);
    questionCatalogRef.current = questionCatalog;
    const [loading, setLoading] = useState(true);
    const {t, i18n} = useTranslation();
    const loggedInUser = useStore((state) => state.loggedInUser);

    // Función para limpiar las fechas
    const clearDates = () => {
        setFromDate(null);
        setToDate(null);
    };

    // Función para obtener los parámetros de fecha para la API
    const getDateParams = () => {
        const params = {};
        if (fromDate) {
            params.dateStart = format(fromDate, "yyyy-MM-dd");
        }
        if (toDate) {
            params.dateEnd = format(toDate, "yyyy-MM-dd");
        }
        return params;
    };

    const loadStatsPageDataBatched = useCallback(async (catalog, safeDateParams) => {
        const effectiveCatalog = catalog ?? {complaint: [], consultation: []};
        const universe = await loadStatsUniverseBatched({
            fromDate: safeDateParams.dateStart,
            toDate: safeDateParams.dateEnd,
            filterByDate: filterCommunicationsByDate,
            questionsByType: effectiveCatalog,
            language: i18n.language,
            typeLabels: {
                complaint: t("communicationTypes.complaint"),
                consultation: t("communicationTypes.consultation"),
            },
            t,
        });
        setUniverseAggregates({
            questionOptionCharts: universe.questionOptionCharts,
            anonymousPies: universe.anonymousPies,
            summary: universe.summary,
        });
    }, [i18n.language, t]);

    const loadStatsPageData = useCallback(async (dateParams = {}, includeQuestionCatalog = false) => {
        setLoading(true);
        const safeDateParams =
            dateParams && typeof dateParams === "object" ? dateParams : {};
        try {
            const [statsResult, loadedQuestionCatalog] = await Promise.all([
                getStatsData(safeDateParams).catch((error) => {
                    console.error("Error loading stats data:", error);
                    return {};
                }),
                includeQuestionCatalog ? loadQuestionCatalog() : Promise.resolve(null),
            ]);

            setData(
                statsResult && typeof statsResult === "object" && !Array.isArray(statsResult)
                    ? statsResult
                    : {}
            );
            if (loadedQuestionCatalog) {
                const catalog = {
                    complaint: normalizeQuestionsResponse(loadedQuestionCatalog.complaint),
                    consultation: normalizeQuestionsResponse(
                        loadedQuestionCatalog.consultation,
                    ),
                };
                setQuestionCatalog(catalog);
                questionCatalogRef.current = catalog;
                await loadStatsPageDataBatched(catalog, safeDateParams);
            } else {
                await loadStatsPageDataBatched(questionCatalogRef.current, safeDateParams);
            }
        } catch (error) {
            console.error("Error loading statistics page data:", error);
            setData((currentData) =>
                currentData && typeof currentData === "object" ? currentData : {}
            );
        } finally {
            setLoading(false);
        }
    }, [loadStatsPageDataBatched]);

    // Función para aplicar el filtro
    const applyFilter = async () => {
        const dateParams = getDateParams();
        await loadStatsPageData(dateParams);
    };

    useEffect(() => {
        const fetchData = async () => {
            await loadStatsPageData({}, true);
        };
        fetchData();
    }, [loadStatsPageData]);

    // Transformar los datos de complaints y consultation en el formato adecuado para los gráficos de líneas
    const lineChartComplaints = Object.entries((data?.communications?.complaint) || {}).map(
        ([month, complaints]) => ({
            month,
            complaints,
        }),
    );

    const lineChartConsultations = Object.entries((data?.communications?.consultation) || {}).map(
        ([month, consultations]) => ({
            month,
            consultations,
        }),
    );

    // Transformar los datos de tipo de comunicación para la torta
    // Ahora calculamos el porcentaje de denuncias vs consultas del total
    const totalCommunications = Object.values((data?.communications?.all) || {}).reduce((sum, count) => sum + count, 0);
    
    const pieChartCommunicationTypes = [];
    if (totalCommunications > 0) {
        const totalComplaints = Object.values((data?.communications?.complaint) || {}).reduce((sum, count) => sum + count, 0);
        const totalConsultations = Object.values((data?.communications?.consultation) || {}).reduce((sum, count) => sum + count, 0);
        
        if (totalComplaints > 0) {
            pieChartCommunicationTypes.push({
                name: t("communicationTypes.complaint"),
                value: totalComplaints
            });
        }
        
        if (totalConsultations > 0) {
            pieChartCommunicationTypes.push({
                name: t("communicationTypes.consultation"),
                value: totalConsultations
            });
        }
    }

    const pieChartAnonymousComplaints =
        universeAggregates.anonymousPies.complaint;
    const pieChartAnonymousConsultations =
        universeAggregates.anonymousPies.consultation;

    // Transformar los datos de consultations en el formato adecuado para el gráfico de torta
    const pieChartConsultations = Object.entries((data?.consultations) || {}).map(
        ([name, value]) => ({
            name,
            value,
        }),
    );

    // Agrupar datos de states para denuncias y consultas por separado
    const getStatesData = (statesData, type) => {
        if (!statesData || !statesData[type]) return {};
        
        const result = {};
        Object.entries(statesData[type]).forEach(([phase, count]) => {
            if (typeof count === "number" && count > 0) {
                result[phase] = count;
            }
        });
        
        return result;
    };

    const statesComplaints = getStatesData(data?.states, 'complaint');
    const statesConsultations = getStatesData(data?.states, 'consultation');

    const pieChartStatesComplaints = Object.entries(statesComplaints)
        .filter(([, value]) => value > 0)
        .map(([name, value]) => ({
            name,
            value,
        }));

    const pieChartStatesConsultations = Object.entries(statesConsultations)
        .filter(([, value]) => value > 0)
        .map(([name, value]) => ({
            name,
            value,
        }));

    // Transformar los datos de visits en el formato adecuado para el gráfico de barras
    const visitsRaw = data?.visits?.visits ?? data?.visits ?? {};
    const barChartData = Object.entries(visitsRaw).map(([month, visits]) => ({
        month,
        visits,
    }));

    // Transformar los datos de stateLogs en el formato adecuado para el gráfico de barras
    // Ahora separamos por complaint y consultation
    const stateLogsDataComplaints = Object.entries((data?.stateLogs?.complaint) || {}).map(([state, count]) => ({
        state,
        count,
    }));

    const stateLogsDataConsultations = Object.entries((data?.stateLogs?.consultation ?? {}) || {}).map(([state, count]) => ({
        state,
        count,
    }));

    // Transformar los datos de tags para las gráficas de tarta
    const pieChartTagsComplaints = data?.tags?.complaint ? 
        Object.entries(data.tags.complaint)
            .filter(([, value]) => value > 0)
            .map(([name, value]) => ({
                name,
                value,
            })) : [];

    const pieChartTagsConsultations = data?.tags?.consultation ? 
        Object.entries(data.tags.consultation)
            .filter(([, value]) => value > 0)
            .map(([name, value]) => ({
                name,
                value,
            })) : [];

    const COLORS = [
        "hsl(var(--chart-1))",
        "hsl(var(--chart-2))",
        "hsl(var(--chart-3))",
        "hsl(var(--chart-4))",
        "hsl(var(--chart-5))",
        "hsl(var(--chart-6))",
        "hsl(var(--chart-7))",
        "hsl(var(--chart-8))",
        "hsl(var(--chart-9))",
        "hsl(var(--chart-10))",
    ];

    const questionOptionCharts = useMemo(() => {
        try {
            return Array.isArray(universeAggregates.questionOptionCharts)
                ? universeAggregates.questionOptionCharts
                : [];
        } catch (error) {
            console.error("Error building question charts:", error);
            return [];
        }
    }, [universeAggregates]);
    const questionChartStorageKey = getQuestionChartStorageKey(loggedInUser);
    const {preferences: chartPreferences, updatePreference: updateChartPreference} =
        useStatsChartPreferences(questionChartStorageKey);

    const standardChartSettings = useMemo(
        () => [
            {
                key: "standard:complaints-timeline",
                title: t("statsPage.graph1.complaintsTitle"),
                group: "standard",
                hasData: lineChartComplaints.some((entry) => entry.complaints > 0),
                defaultVisible: true,
                available: true,
            },
            {
                key: "standard:consultations-timeline",
                title: t("statsPage.graph1.consultationsTitle"),
                group: "standard",
                hasData: lineChartConsultations.some((entry) => entry.consultations > 0),
                defaultVisible: true,
                available: lineChartConsultations.length > 0,
            },
            {
                key: "standard:communication-types",
                title: t("statsPage.graph4.title"),
                group: "standard",
                hasData: pieChartCommunicationTypes.some((entry) => entry.value > 0),
                defaultVisible: true,
                available: Boolean(data?.communications?.all),
            },
            {
                key: "standard:anonymous-complaints",
                title: `${t("statsPage.graph3.title")} - ${t("communicationTypes.complaint")}`,
                group: "standard",
                hasData: pieChartAnonymousComplaints.some((entry) => entry.value > 0),
                defaultVisible: true,
                available: pieChartAnonymousComplaints.length > 0,
            },
            {
                key: "standard:anonymous-consultations",
                title: `${t("statsPage.graph3.title")} - ${t("communicationTypes.consultation")}`,
                group: "standard",
                hasData: pieChartAnonymousConsultations.some((entry) => entry.value > 0),
                defaultVisible: true,
                available: pieChartAnonymousConsultations.length > 0,
            },
            {
                key: "standard:states-complaints",
                title: `${t("statsPage.graph5.title")} - ${t("communicationTypes.complaint")}`,
                group: "standard",
                hasData: pieChartStatesComplaints.some((entry) => entry.value > 0),
                defaultVisible: true,
                available: pieChartStatesComplaints.length > 0,
            },
            {
                key: "standard:states-consultations",
                title: `${t("statsPage.graph5.title")} - ${t("communicationTypes.consultation")}`,
                group: "standard",
                hasData: pieChartStatesConsultations.some((entry) => entry.value > 0),
                defaultVisible: true,
                available: pieChartStatesConsultations.length > 0,
            },
            {
                key: "standard:visits",
                title: t("statsPage.graph6.title"),
                group: "standard",
                hasData: barChartData.some((entry) => entry.visits > 0),
                defaultVisible: true,
                available: true,
            },
            {
                key: "standard:state-logs-complaints",
                title: `${t("statsPage.graph7.title")} - ${t("communicationTypes.complaint")}`,
                group: "standard",
                hasData: stateLogsDataComplaints.some((entry) => entry.count > 0),
                defaultVisible: true,
                available: Boolean(data?.stateLogs?.complaint) && stateLogsDataComplaints.length > 0,
            },
            {
                key: "standard:state-logs-consultations",
                title: `${t("statsPage.graph7.title")} - ${t("communicationTypes.consultation")}`,
                group: "standard",
                hasData: stateLogsDataConsultations.some((entry) => entry.count > 0),
                defaultVisible: true,
                available: Boolean(data?.stateLogs?.consultation) && stateLogsDataConsultations.length > 0,
            },
            {
                key: "standard:tags-complaints",
                title: t("statsPage.graph8.title"),
                group: "standard",
                hasData: pieChartTagsComplaints.some((entry) => entry.value > 0),
                defaultVisible: true,
                available: Boolean(data?.tags?.complaint) && pieChartTagsComplaints.length > 0,
            },
            {
                key: "standard:tags-consultations",
                title: t("statsPage.graph9.title"),
                group: "standard",
                hasData: pieChartTagsConsultations.some((entry) => entry.value > 0),
                defaultVisible: true,
                available: Boolean(data?.tags?.consultation) && pieChartTagsConsultations.length > 0,
            },
        ],
        [
            barChartData,
            data,
            lineChartComplaints,
            lineChartConsultations,
            pieChartAnonymousComplaints,
            pieChartAnonymousConsultations,
            pieChartCommunicationTypes,
            pieChartStatesComplaints,
            pieChartStatesConsultations,
            pieChartTagsComplaints,
            pieChartTagsConsultations,
            stateLogsDataComplaints,
            stateLogsDataConsultations,
            t,
        ],
    );

    const chartSettings = useMemo(() => {
        try {
            const safeStandardCharts = Array.isArray(standardChartSettings)
                ? standardChartSettings
                : [];
            const safeQuestionCharts = Array.isArray(questionOptionCharts)
                ? questionOptionCharts
                : [];
            return [
                ...safeStandardCharts.filter((chart) => chart?.available),
                ...safeQuestionCharts
                    .filter(
                        (chart) =>
                            chart && typeof chart.key === "string" && chart.key !== "",
                    )
                    .map((chart) => ({
                        key: chart.key,
                        title: String(chart.title ?? chart.key),
                        group: "question",
                        hasData: Boolean(chart.hasData),
                        defaultVisible: Boolean(chart.hasData),
                        supportsType: true,
                    })),
            ];
        } catch (error) {
            console.error("Error building chart settings:", error);
            return [];
        }
    }, [questionOptionCharts, standardChartSettings]);

    const isStandardChartVisible = (key) => {
        try {
            return getStatsChartVisibility(chartPreferences ?? {}, {
                key,
                defaultVisible: true,
            });
        } catch (error) {
            return true;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center h-screen">
                <Loader style={"loader"}/>
            </div>
        );
    }

    return (
        <div className="p-8">
            <section className="mb-6 flex items-center justify-between gap-4">
                <h1 className="text-3xl font-bold">{t("statsPage.title")}</h1>
                <StatsChartSettings
                    charts={chartSettings}
                    preferences={chartPreferences}
                    onUpdatePreference={updateChartPreference}
                />
            </section>

            <section className="mb-6 flex flex-wrap items-end gap-4" aria-label={t("statsPage.graph1.filters")}>
                <div>
                    <label htmlFor="stats-date-from" className="block text-sm font-medium text-gray-700">
                        {t("statsPage.graph1.dateFrom")}
                    </label>
                    <Popover>
                        <PopoverTrigger asChild={true}>
                            <Button
                                id="stats-date-from"
                                variant="outline"
                                className={cn(
                                    "w-[240px] justify-start text-left font-normal",
                                    !fromDate && "text-muted-foreground",
                                )}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4"/>
                                {fromDate ? (
                                    format(fromDate, "PPP", {locale: es})
                                ) : (
                                    <span>{t("statsPage.graph1.dateDescription")}</span>
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                locale={es}
                                selected={fromDate}
                                onSelect={(date) => {
                                    setFromDate(date);
                                }}
                                initialFocus={true}
                                disabled={(date) => date > currentDate}
                            />
                        </PopoverContent>
                    </Popover>
                </div>

                <div>
                    <label htmlFor="stats-date-to" className="block text-sm font-medium text-gray-700">
                        {t("statsPage.graph1.dateTo")}
                    </label>
                    <Popover>
                        <PopoverTrigger asChild={true}>
                            <Button
                                id="stats-date-to"
                                variant="outline"
                                className={cn(
                                    "w-[240px] justify-start text-left font-normal",
                                    !toDate && "text-muted-foreground",
                                )}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4"/>
                                {toDate ? (
                                    format(toDate, "PPP", {locale: es})
                                ) : (
                                    <span>{t("statsPage.graph1.dateDescription")}</span>
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={toDate}
                                locale={es}
                                onSelect={(date) => {
                                    setToDate(date);
                                }}
                                initialFocus={true}
                                disabled={(date) => date > currentDate}
                            />
                        </PopoverContent>
                    </Popover>
                </div>

                <Button
                    variant="outline"
                    onClick={clearDates}
                    className="h-10 px-3"
                    disabled={!fromDate && !toDate}
                >
                    <X className="mr-2 h-4 w-4" />
                    {t("statsPage.graph1.clearDates") || "Borrar fechas"}
                </Button>
                <Button
                    variant="default"
                    onClick={applyFilter}
                    className="h-10 px-3"
                >
                    {t("statsPage.graph1.filter") || "Filtrar"}
                </Button>
            </section>

            {isStandardChartVisible("standard:complaints-timeline") && (
            <section
                className="my-4"
                data-testid="stats-chart-standard-complaints-timeline"
                data-has-data={lineChartComplaints.some((entry) => entry.complaints > 0) ? "true" : "false"}
            >
                <Card>
                    <CardHeader>
                        <CardTitle>{t("statsPage.graph1.complaintsTitle")}</CardTitle>
                        <CardDescription>
                            {t("statsPage.graph1.description")}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[400px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={lineChartComplaints}
                                    margin={{
                                        top: 24,
                                        left: 12,
                                        right: 12,
                                    }}
                                >
                                    <CartesianGrid vertical={false}/>
                                    <XAxis
                                        dataKey="month"
                                        tickLine={false}
                                        axisLine={false}
                                        tickMargin={8}
                                        tickFormatter={(value) => value}
                                    />
                                    <YAxis
                                        stroke="#888888"
                                        fontSize={12}
                                        tickLine={false}
                                        axisLine={false}
                                        domain={[0, 'dataMax + 1']}
                                        tickFormatter={(value) => `${value}`}
                                    />
                                    <Tooltip
                                        formatter={(value, name) => [
                                            value,
                                            name === "complaints" ? t("statsPage.graph1.complaints") : name,
                                        ]}/>
                                    <Line
                                        dataKey="complaints"
                                        type="natural"
                                        stroke="hsl(var(--chart-1))"
                                        strokeWidth={2}
                                        dot={{
                                            fill: "hsl(var(--chart-1))",
                                        }}
                                        activeDot={{
                                            r: 6,
                                        }}
                                    >
                                        <LabelList dataKey="complaints" position="top" offset={10} formatter={(value) => (value === 0 ? "" : value)} />
                                    </Line>
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </section>
            )}

            {lineChartConsultations.length > 0 && isStandardChartVisible("standard:consultations-timeline") && (
                <section
                    className="my-4"
                    data-testid="stats-chart-standard-consultations-timeline"
                    data-has-data={lineChartConsultations.some((entry) => entry.consultations > 0) ? "true" : "false"}
                >
                    <Card>
                        <CardHeader>
                            <div className="flex flex-wrap justify-between gap-x-4">
                                <div className="">
                                    <CardTitle>{t("statsPage.graph1.consultationsTitle")}</CardTitle>
                                    <CardDescription>
                                        {t("statsPage.graph1.description")}
                                    </CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={lineChartConsultations}
                                        margin={{ top: 24, left: 12, right: 12 }}
                                    >
                                        <CartesianGrid vertical={false}/>
                                        <XAxis
                                            dataKey="month"
                                            tickLine={false}
                                            axisLine={false}
                                            tickMargin={8}
                                            tickFormatter={(value) => value}
                                        />
                                        <YAxis
                                            stroke="#888888"
                                            fontSize={12}
                                            tickLine={false}
                                            axisLine={false}
                                            domain={[0, 'dataMax + 1']}
                                            tickFormatter={(value) => `${value}`}
                                        />
                                        <Tooltip
                                            formatter={(value, name) => [
                                                value,
                                                name === "consultations" ? t("statsPage.graph1.consultations") : name,
                                            ]}
                                        />
                                        <Line
                                            dataKey="consultations"
                                            type="natural"
                                            stroke="hsl(var(--chart-2))"
                                            strokeWidth={2}
                                            dot={{ fill: "hsl(var(--chart-2))" }}
                                            activeDot={{ r: 6 }}
                                        >
                                            <LabelList dataKey="consultations" position="top" offset={10} formatter={(value) => (value === 0 ? "" : value)} />
                                        </Line>
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </section>
            )}

            <QuestionOptionCharts
                key={questionChartStorageKey}
                charts={questionOptionCharts}
                colors={COLORS}
                preferences={chartPreferences}
            />

            {/* Gráfico de tipos de comunicación */}
            {data?.communications?.all && isStandardChartVisible("standard:communication-types") && (
                <section
                    className="my-4"
                    data-testid="stats-chart-standard-communication-types"
                    data-has-data={pieChartCommunicationTypes.length > 0 ? "true" : "false"}
                >
                    <Card>
                        <CardHeader>
                            <CardTitle>{t("statsPage.graph4.title")}</CardTitle>
                            <CardDescription>
                                {t("statsPage.graph4.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {pieChartCommunicationTypes.length > 0 ? (
                                <div className="h-[400px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pieChartCommunicationTypes}
                                                cx="50%"
                                                cy="50%"
                                                label={({name, percent}) =>
                                                    `${name} (${(percent * 100).toFixed(0)}%)`
                                                }
                                                outerRadius={150}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                {pieChartCommunicationTypes.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={COLORS[index % COLORS.length]}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value, name) => [
                                                    value,
                                                    name,
                                                ]}
                                            />
                                            <Legend
                                                formatter={(value) => value}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div className="h-[400px] w-full flex items-center justify-center">
                                    <p className="text-muted-foreground text-center">
                                        {t("statsPage.noDataMessage") || "No hay datos de tipos de comunicaciones disponibles para el período seleccionado."}
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </section>
            )}

            {/* Gráfico de denuncias anónimas para denuncias */}
            {pieChartAnonymousComplaints.length > 0 && isStandardChartVisible("standard:anonymous-complaints") && (
                <section
                    className="my-4"
                    data-testid="stats-anonymity-chart-complaint"
                    data-chart-testid="stats-chart-standard-anonymous-complaints"
                    data-has-data="true"
                >
                    <Card>
                        <CardHeader>
                            <CardTitle>{t("statsPage.graph3.title")} - {t("communicationTypes.complaint")}</CardTitle>
                            <CardDescription>
                                {t("statsPage.graph3.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieChartAnonymousComplaints}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={true}
                                            label={({name, percent}) =>
                                                `${name} (${(percent * 100).toFixed(0)}%)`
                                            }
                                            outerRadius={150}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {pieChartAnonymousComplaints.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={COLORS[index % COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip/>
                                        <Legend/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </section>
            )}

            {/* Gráfico de denuncias anónimas para consultas */}
            {pieChartAnonymousConsultations.length > 0 && isStandardChartVisible("standard:anonymous-consultations") && (
                <section
                    className="my-4"
                    data-testid="stats-anonymity-chart-consultation"
                    data-chart-testid="stats-chart-standard-anonymous-consultations"
                    data-has-data="true"
                >
                    <Card>
                        <CardHeader>
                            <CardTitle>{t("statsPage.graph3.title")} - {t("communicationTypes.consultation")}</CardTitle>
                            <CardDescription>
                                {t("statsPage.graph3.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieChartAnonymousConsultations}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={true}
                                            label={({name, percent}) =>
                                                `${name} (${(percent * 100).toFixed(0)}%)`
                                            }
                                            outerRadius={150}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {pieChartAnonymousConsultations.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={COLORS[index % COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip/>
                                        <Legend/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </section>
            )}



            {/* Gráfico de fases para denuncias */}
            {pieChartStatesComplaints.length > 0 && isStandardChartVisible("standard:states-complaints") && (
                <section className="my-4" data-testid="stats-chart-standard-states-complaints" data-has-data="true">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t("statsPage.graph5.title")} - {t("communicationTypes.complaint")}</CardTitle>
                            <CardDescription>
                                {t("statsPage.graph5.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieChartStatesComplaints}
                                            cx="50%"
                                            cy="50%"
                                            label={({name, value}) =>
                                                `${name} (${value})`
                                            }
                                            labelLine={true}
                                            outerRadius={150}
                                            fill="#8884d8"
                                            dataKey="value"
                                            paddingAngle={5}
                                        >
                                            {pieChartStatesComplaints.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={COLORS[index % COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip/>
                                        <Legend/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </section>
            )}

            {/* Gráfico de fases para consultas */}
            {pieChartStatesConsultations.length > 0 && isStandardChartVisible("standard:states-consultations") && (
                <section className="my-4" data-testid="stats-chart-standard-states-consultations" data-has-data="true">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t("statsPage.graph5.title")} - {t("communicationTypes.consultation")}</CardTitle>
                            <CardDescription>
                                {t("statsPage.graph5.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieChartStatesConsultations}
                                            cx="50%"
                                            cy="50%"
                                            label={({name, value}) =>
                                                `${name} (${value})`
                                            }
                                            labelLine={true}
                                            outerRadius={150}
                                            fill="#8884d8"
                                            dataKey="value"
                                            paddingAngle={5}
                                        >
                                            {pieChartStatesConsultations.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={COLORS[index % COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip/>
                                        <Legend/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </section>
            )}

            {isStandardChartVisible("standard:visits") && (
            <section
                className="my-4"
                data-testid="stats-chart-standard-visits"
                data-has-data={barChartData.some((entry) => entry.visits > 0) ? "true" : "false"}
            >
                <Card>
                    <CardHeader>
                        <CardTitle>{t("statsPage.graph6.title")}</CardTitle>
                        <CardDescription>
                            {t("statsPage.graph6.description")}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[400px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={barChartData}
                                    margin={{ top: 32, right: 16, left: 8, bottom: 0 }}
                                >
                                    <CartesianGrid vertical={false}/>
                                    <XAxis
                                        dataKey="month"
                                        stroke="#888888"
                                        fontSize={12}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        stroke="#888888"
                                        fontSize={12}
                                        tickLine={false}
                                        axisLine={false}
                                        tickFormatter={(value) => `${value}`}
                                    />
                                    <Tooltip
                                        formatter={(value, name) => [
                                            value,
                                            name === "visits" ? t("statsPage.graph6.visits") : name,
                                        ]}
                                    />
                                    <Bar
                                        dataKey="visits"
                                        fill="hsl(var(--primary))"
                                        radius={[4, 4, 0, 0]}
                                    >
                                        <LabelList
                                            dataKey="visits"
                                            position="top"
                                            fill="#000000"
                                            formatter={(value) => (value === 0 ? "" : value)}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </section>
            )}

            {/* Gráfico de tiempo promedio por estado para denuncias */}
            {data?.stateLogs?.complaint && stateLogsDataComplaints.length > 0 && isStandardChartVisible("standard:state-logs-complaints") && (
                <section className="my-4" data-testid="stats-chart-standard-state-logs-complaints" data-has-data="true">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t("statsPage.graph7.title")} - {t("communicationTypes.complaint")}</CardTitle>
                            <CardDescription>
                                {t("statsPage.graph7.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={stateLogsDataComplaints}
                                        margin={{ top: 32, right: 16, left: 8, bottom: 0 }}
                                    >
                                        <CartesianGrid vertical={false}/>
                                        <XAxis
                                            dataKey="state"
                                            stroke="#888888"
                                            fontSize={12}
                                            tickLine={false}
                                            axisLine={false}
                                        />
                                        <YAxis
                                            stroke="#888888"
                                            fontSize={12}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(value) => `${value}`}
                                        />
                                        <Tooltip
                                            formatter={(value, name) => [
                                                `${value} ${t("general.days")}`,
                                                name === "count" ? t("statsPage.graph7.stateLogs") : name,
                                            ]}
                                        />
                                        <Bar
                                            dataKey="count"
                                            fill="hsl(var(--chart-3))"
                                            radius={[4, 4, 0, 0]}
                                        >
                                            <LabelList
                                                dataKey="count"
                                                position="top"
                                                fill="#000000"
                                                formatter={(value) => (value === 0 ? "" : `${value} ${t("general.days")}`)}
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </section>
            )}

            {/* Gráfico de tiempo promedio por estado para consultas */}
            {data?.stateLogs?.consultation && stateLogsDataConsultations.length > 0 && isStandardChartVisible("standard:state-logs-consultations") && (
                <section className="my-4" data-testid="stats-chart-standard-state-logs-consultations" data-has-data="true">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t("statsPage.graph7.title")} - {t("communicationTypes.consultation")}</CardTitle>
                            <CardDescription>
                                {t("statsPage.graph7.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={stateLogsDataConsultations}
                                        margin={{ top: 32, right: 16, left: 8, bottom: 0 }}
                                    >
                                        <CartesianGrid vertical={false}/>
                                        <XAxis
                                            dataKey="state"
                                            stroke="#888888"
                                            fontSize={12}
                                            tickLine={false}
                                            axisLine={false}
                                        />
                                        <YAxis
                                            stroke="#888888"
                                            fontSize={12}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(value) => `${value}`}
                                        />
                                        <Tooltip
                                            formatter={(value, name) => [
                                                `${value} ${t("general.days")}`,
                                                name === "count" ? t("statsPage.graph7.stateLogs") : name,
                                            ]}
                                        />
                                        <Bar
                                            dataKey="count"
                                            fill="hsl(var(--chart-4))"
                                            radius={[4, 4, 0, 0]}
                                        >
                                            <LabelList
                                                dataKey="count"
                                                position="top"
                                                fill="#000000"
                                                formatter={(value) => (value === 0 ? "" : `${value} ${t("general.days")}`)}
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </section>
            )}

            {/* Gráfico de etiquetas para denuncias */}
            {data?.tags?.complaint && pieChartTagsComplaints.length > 0 && isStandardChartVisible("standard:tags-complaints") && (
                <section className="my-4" data-testid="stats-chart-standard-tags-complaints" data-has-data="true">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t("statsPage.graph8.title")}</CardTitle>
                            <CardDescription>
                                {t("statsPage.graph8.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieChartTagsComplaints}
                                            cx="50%"
                                            cy="50%"
                                            label={({name, percent}) =>
                                                `${name} (${(percent * 100).toFixed(0)}%)`
                                            }
                                            labelLine={true}
                                            outerRadius={150}
                                            fill="#8884d8"
                                            dataKey="value"
                                            paddingAngle={5}
                                        >
                                            {pieChartTagsComplaints.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={COLORS[index % COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip/>
                                        <Legend/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </section>
            )}

            {/* Gráfico de etiquetas para consultas */}
            {data?.tags?.consultation && pieChartTagsConsultations.length > 0 && isStandardChartVisible("standard:tags-consultations") && (
                <section className="my-4" data-testid="stats-chart-standard-tags-consultations" data-has-data="true">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t("statsPage.graph9.title")}</CardTitle>
                            <CardDescription>
                                {t("statsPage.graph9.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieChartTagsConsultations}
                                            cx="50%"
                                            cy="50%"
                                            label={({name, percent}) =>
                                                `${name} (${(percent * 100).toFixed(0)}%)`
                                            }
                                            labelLine={true}
                                            outerRadius={150}
                                            fill="#8884d8"
                                            dataKey="value"
                                            paddingAngle={5}
                                        >
                                            {pieChartTagsConsultations.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={COLORS[index % COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip/>
                                        <Legend/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </section>
            )}

            <StatsSummaryTable
                summary={universeAggregates.summary}
                communicationsByType={null}
                questionsByType={questionCatalog}
                language={i18n.language}
            />
        </div>
    );
};

export default StatsPage;
